// What the firm actually mailed. Every send goes through one door and
// leaves a row, so this is where "did the invitation go, and where to"
// gets an answer - including the ones that did not go, which is the half
// no product surface showed before.

import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  clearEmailDeliveries,
  fetchEmailDeliveries,
  MAIL_KINDS,
  type EmailDeliveryRow,
  type MailKind,
} from "../../api/emailDeliveries";
import { useCurrentUser } from "../../components/auth/CurrentUserContext";
import { isAdminRole } from "../../lib/roles";
import { Skeleton, SkeletonGroup } from "../../components/chrome/Skeleton";
import { SettingsNav } from "./SettingsNav";
import { fmtDate } from "../../lib/dates";

const KIND_LABEL: Record<MailKind, string> = {
  invitation: "Portal invitation",
  otp: "Sign-in code",
  invoice: "Invoice",
  digest: "Staff digest",
  alert: "Staff alert",
  auth: "Password link",
  other: "Other",
};

// Only a failure is red. A skipped send is not a fault of the message -
// mail was never connected - and it needs a different move from the
// office, so it must not read as the same event.
const STATUS_TONE: Record<EmailDeliveryRow["status"], string> = {
  queued: "var(--warn-600)",
  sent: "var(--ok-600)",
  failed: "var(--red-700)",
  skipped: "var(--warn-600)",
};

const STATUS_LABEL: Record<EmailDeliveryRow["status"], string> = {
  queued: "Retrying",
  sent: "Sent",
  failed: "Failed",
  skipped: "Not sent",
};

export function EmailLog() {
  const user = useCurrentUser();
  const isAdmin = isAdminRole(user?.role);
  const [kind, setKind] = useState<MailKind | "">("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);

  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["email-deliveries", kind, status, page],
    queryFn: () => fetchEmailDeliveries({ kind, status, page }),
    enabled: isAdmin,
    staleTime: 15_000,
  });

  const clearMut = useMutation({
    mutationFn: clearEmailDeliveries,
    onSuccess: async () => {
      setPage(1);
      await queryClient.invalidateQueries({ queryKey: ["email-deliveries"] });
    },
  });

  function onClear() {
    if (
      !window.confirm(
        "Clear the whole email log, including rows outside the current filter? " +
          "Rows still being retried stay; everything else is deleted for good.",
      )
    ) {
      return;
    }
    clearMut.mutate();
  }

  if (!isAdmin) {
    return (
      <main className="cc-main">
        <div className="cc-body settings-body">
          <SettingsNav />
          <section className="cc-card">
            <h3>Email log</h3>
            <p className="settings-sub">
              {user
                ? "Admin access is required to read the mail log."
                : "Could not confirm your account. Refresh to retry."}
            </p>
          </section>
        </div>
      </main>
    );
  }

  const rows = data?.deliveries ?? [];

  return (
    <main className="cc-main">
      <div className="cc-body settings-body">
        <SettingsNav />
        <header className="settings-head">
          <div>
            <h2>Email log</h2>
            <p className="settings-sub">
              Every message the firm sent a client or a colleague. Subjects and addresses only -
              codes and case content live in the body, so it is kept sealed while a message is
              waiting to be retried and deleted once it is not.
            </p>
          </div>
        </header>

        <section className="cc-card settings-log">
          <div className="cc-card-head">
            <div>
              <div className="cc-card-title">Deliveries</div>
              <div className="cc-card-sub">
                {data ? `${data.total} in total` : "Reading the log"}
              </div>
            </div>
            <div className="actions">
              <select
                className="cc-field"
                aria-label="Filter by kind"
                value={kind}
                onChange={(e) => {
                  setKind(e.target.value as MailKind | "");
                  setPage(1);
                }}
              >
                <option value="">Every kind</option>
                {MAIL_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {KIND_LABEL[k]}
                  </option>
                ))}
              </select>
              <select
                className="cc-field"
                aria-label="Filter by outcome"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">Sent and not sent</option>
                <option value="sent">Sent</option>
                <option value="queued">Retrying</option>
                <option value="failed">Failed</option>
                <option value="skipped">Not sent</option>
              </select>
              <button
                type="button"
                className="cc-link-btn danger"
                disabled={clearMut.isPending}
                onClick={onClear}
              >
                {clearMut.isPending ? "Clearing..." : "Clear whole log"}
              </button>
            </div>
          </div>

          {isLoading && (
            <div style={{ padding: "12px 18px" }}>
              <SkeletonGroup label="Reading the mail log">
                <Skeleton width="70%" />
                <Skeleton width="55%" />
                <Skeleton width="60%" />
              </SkeletonGroup>
            </div>
          )}

          {isError && (
            <div style={{ padding: "12px 18px", fontSize: 12.5, color: "var(--muted)" }}>
              We could not read the mail log.
            </div>
          )}

          {clearMut.isError && (
            <div style={{ padding: "12px 18px", fontSize: 12.5, color: "var(--red-700)" }}>
              We could not clear the log. Try again.
            </div>
          )}

          {clearMut.isSuccess && (
            <div style={{ padding: "12px 18px", fontSize: 12.5, color: "var(--muted)" }}>
              Removed {clearMut.data} rows.
            </div>
          )}

          {data && rows.length === 0 && (
            <div style={{ padding: "12px 18px", fontSize: 12.5, color: "var(--muted)" }}>
              Nothing has gone out under this filter.
            </div>
          )}

          {rows.map((row) => (
            <div
              key={row._id}
              style={{
                display: "flex",
                gap: 12,
                alignItems: "baseline",
                flexWrap: "wrap",
                padding: "9px 18px",
                borderTop: "1px solid var(--line)",
                fontSize: 13,
              }}
            >
              <span style={{ color: STATUS_TONE[row.status], fontWeight: 600, minWidth: 64 }}>
                {STATUS_LABEL[row.status]}
              </span>
              <span style={{ minWidth: 120, color: "var(--muted)", fontSize: 12.5 }}>
                {KIND_LABEL[row.kind]}
              </span>
              <span style={{ flex: 1, minWidth: 220 }}>
                {row.subject}
                <span style={{ color: "var(--muted)" }}> to {row.to}</span>
                {row.error && (
                  <span style={{ display: "block", color: "var(--red-700)", fontSize: 12 }}>
                    {row.error}
                    {/* Whether the office still has to do something about
                        it depends entirely on this. */}
                    {row.status === "queued" && ` - try ${row.attempts} failed, another is due`}
                  </span>
                )}
              </span>
              {row.caseEId && (
                <Link className="cc-link-subtle tnum-mono" to={`/matter/${row.caseEId}`}>
                  {row.caseEId}
                </Link>
              )}
              <span className="tnum" style={{ color: "var(--muted)", fontSize: 12 }}>
                {fmtDate(row.createdAt)}
              </span>
            </div>
          ))}

          {data && data.totalPages > 1 && (
            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                padding: "9px 18px",
                borderTop: "1px solid var(--line)",
                fontSize: 12.5,
              }}
            >
              <button
                type="button"
                className="cc-link-btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Newer
              </button>
              <span style={{ color: "var(--muted)" }}>
                Page {data.page} of {data.totalPages}
              </span>
              <button
                type="button"
                className="cc-link-btn"
                disabled={page >= data.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Older
              </button>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
